/**
 * Legal names (Phase 27). Every account carries the name exactly as spelled on the official document
 * (National ID / Iqama / passport) in two fields:
 *   - `full_name`     English spelling (also the compatibility field older records already have)
 *   - `full_name_ar`  Arabic spelling. A passport-only holder without an Arabic spelling enters the English spelling here.
 * The UI shows the spelling that matches the interface language and falls back to the other one.
 */

/** Language-preferred legal name with fallback; never returns undefined for a user object. */
export function displayName(user, lang = activeLang) {
  if (!user) return "";
  const en = (user.full_name || "").trim();
  const ar = (user.full_name_ar || "").trim();
  if (lang === "ar") return ar || en;
  return en || ar;
}

/** Same rule for records that embed the two spellings as flat fields (e.g. proposal `created_by_name` / `created_by_name_ar`). */
export function pickName(en, ar, lang = activeLang) {
  const e = (en || "").trim();
  const a = (ar || "").trim();
  return lang === "ar" ? a || e : e || a;
}

/** The spelling NOT shown by displayName (empty when both are identical, e.g. passport-only accounts, or one is missing). */
export function otherSpelling(user, lang = activeLang) {
  if (!user) return "";
  const shown = displayName(user, lang);
  const other = lang === "ar" ? (user.full_name || "").trim() : (user.full_name_ar || "").trim();
  return other && other !== shown ? other : "";
}

/** First letter for avatar badges (falls back to the e-mail). */
export function nameInitial(user, lang = activeLang) {
  return (displayName(user, lang) || user?.email || "?").slice(0, 1).toUpperCase();
}

// The interface language, mirrored here by LanguageProvider on every render so name helpers can default to it
// without threading `lang` through every component (they all re-render on a language change anyway, via `t`).
let activeLang = "en";
export const setActiveLang = (lang) => { activeLang = lang === "ar" ? "ar" : "en"; };

/** True when the account still lacks an Arabic spelling (drives the one-time completion prompt for legacy accounts). */
export const needsArabicName = (user) => Boolean(user) && !(user.full_name_ar || "").trim();

/** A legal-name field is complete when it has at least two characters after trimming. */
export const validName = (v) => (v || "").trim().length >= 2;

/** Ledger account labels for the interface language: Arabic spellings (`labels_ar`) overlay the English ones when present. */
export function accountLabels(data, lang = activeLang) {
  if (!data?.labels) return data?.labels;
  return lang === "ar" && data.labels_ar ? { ...data.labels, ...data.labels_ar } : data.labels;
}
